import {AfterViewInit, Component, Input, OnInit} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Tab, TabList, TabPanel, TabPanels, Tabs } from 'primeng/tabs';
import { Subject, takeUntil } from 'rxjs';
import { DecoderService } from '../../../services/decoder.service';
import { DeviceProfileService } from '../../../services/device-profile.service';
import { DeviceService } from '../../../services/device.service';
import { DropdownSelector } from '../../../shared/components/dropdown-selector/dropdown-selector';
import { ImageGallery } from '../../../shared/components/image-gallery/image-gallery';
import { InputSelectButton } from '../../../shared/components/input-select-button/input-select-button';
import { InputTextComponent } from '../../../shared/components/input-text/input-text.component';
import { InputTextarea } from '../../../shared/components/input-textarea/input-textarea';
import { TranslateSafePipe } from '../../../shared/pipes/translation.pipe';
import {dateToString, formatTimeAgoTranslation} from '../../../shared/utils/date.util';
import { DecoderDto } from '../../../types/dtos/decoder.dto';
import { DeviceDto, DeviceImageDto } from '../../../types/dtos/device.dto';
import { DeviceProfileDto } from '../../../types/dtos/deviceProfile.dto';
import {
  InputDropdownSelectorInterface,
  InputSelectButtonInterface,
  InputTextAreaInterface,
  InputTextInterface,
} from '../../../types/interfaces/input.interface';
import { TableRowInterface } from '../../../types/interfaces/table.interface';
import { DeviceDetailAlarmHistory } from './device-detail-alarm-history/device-detail-alarm-history';
import { DeviceDetailConsumption } from './device-detail-consumption/device-detail-consumption';
import {TranslateService} from '@ngx-translate/core';
import {LogService} from '../../../services/log.service';
import {LoggingActionEnum, LoggingTargetEnum} from '../../../types/enums/logging.enum';

@Component({
  selector: 'app-dialog-device-detail',
  imports: [
    Tabs,
    TabList,
    Tab,
    InputTextComponent,
    ImageGallery,
    InputSelectButton,
    InputTextarea,
    DropdownSelector,
    TabPanels,
    TabPanel,
    DeviceDetailAlarmHistory,
    DeviceDetailConsumption,
    TranslateSafePipe,
  ],
  templateUrl: './dialog-device-detail.html',
  styleUrl: './dialog-device-detail.css',
})
export class DialogDeviceDetail implements OnInit,AfterViewInit {

  public readonly defaultFromController = {
    value: '',
    disabled: true,
  };

  public deviceInformationForm: FormGroup = new FormGroup({
    serial_number: new FormControl(this.defaultFromController),
    device_eui: new FormControl(this.defaultFromController),
    manufacturer: new FormControl(this.defaultFromController),
    model: new FormControl(this.defaultFromController),
    decoder: new FormControl(this.defaultFromController),
    address: new FormControl(this.defaultFromController),
    installed_at: new FormControl(this.defaultFromController),
    installation_district: new FormControl(this.defaultFromController),
    comments: new FormControl(this.defaultFromController),
    status: new FormControl(this.defaultFromController),
    last_sig_send: new FormControl(this.defaultFromController),
    pipeSize: new FormControl(this.defaultFromController),
    pipeDn: new FormControl(this.defaultFromController),
    rotation: new FormControl(this.defaultFromController),
    pipeType: new FormControl(this.defaultFromController),
    locationRoom: new FormControl(this.defaultFromController),
  });

  public serialNumberConfig: InputTextInterface = {
    formGroup: this.deviceInformationForm,
    isTranslated: true,
    label: 'common.input.serielNumber.label',
    type: 'text',
    id: 'serial_number',
    iconLeft: 'devices',
  };

  public deviceEuiConfig: InputTextInterface = {
    formGroup: this.deviceInformationForm,
    isTranslated: true,
    label: 'common.input.deviceEui.label',
    type: 'text',
    id: 'device_eui',
    iconLeft: 'code',
  };

  public manufacturerConfig: InputTextInterface = {
    formGroup: this.deviceInformationForm,
    isTranslated: true,
    label: 'common.input.manufacturer.label',
    id: 'manufacturer',
    type: 'text',
    iconLeft: 'factory',
  };

  public modelConfig: InputTextInterface = {
    formGroup: this.deviceInformationForm,
    isTranslated: true,
    label: 'common.input.model.label',
    id: 'model',
    type: 'text',
    iconLeft: 'view_in_ar',
  };

  public decoderConfig: InputTextInterface = {
    formGroup: this.deviceInformationForm,
    isTranslated: true,
    label: 'common.input.decoder.label',
    id: 'decoder',
    type: 'text',
    iconLeft: 'code',
  };

  public commentsConfig: InputTextAreaInterface = {
    formGroup: this.deviceInformationForm,
    isTranslated: true,
    label: 'common.input.comments.label',
    id: 'comments',
    iconLeft: 'comment',
    infoText: 'common.input.comments.info',
  };

  public addressConfig: InputTextInterface = {
    formGroup: this.deviceInformationForm,
    isTranslated: true,
    label: 'common.input.address.label',
    id: 'address',
    type: 'text',
    iconLeft: 'location_on',
  };

  public installationDateConfig: InputTextInterface = {
    formGroup: this.deviceInformationForm,
    isTranslated: true,
    label: 'common.input.installed_at.label',
    id: 'installed_at',
    iconLeft: 'event',
    type: 'text',
  };

  public installationDistrictConfig: InputDropdownSelectorInterface = {
    formGroup: this.deviceInformationForm,
    isTranslated: true,
    label: 'common.input.installation_district.label',
    id: 'installation_district',
    suggestions: [],
    infoText: 'common.input.installation_district.info',
  };

  public lastSigSendConfig: InputTextInterface = {
    formGroup: this.deviceInformationForm,
    isTranslated: true,
    label: 'common.input.last_sig_send.label',
    id: 'last_sig_send',
    type: 'text',
    iconLeft: 'schedule',
  };

  public deviceSizeConfig: InputSelectButtonInterface = {
    formGroup: this.deviceInformationForm,
    isTranslated: true,
    label: 'common.input.pipeSize.label',
    id: 'pipeSize',
    iconLeft: 'straighten',
    options: [
      {
        label: '1/2"',
        value: '1/2',
      },
      {
        label: '3/4"',
        value: '3/4',
      },
      {
        label: '1"',
        value: '1',
      },
      {
        label: '1 1/4"',
        value: '1 1/4',
      },
      {
        label: '1 1/2"',
        value: '1 1/2',
      },
      {
        label: '2"',
        value: '2',
      },
    ],
  };

  public deviceDNConfig: InputSelectButtonInterface = {
    formGroup: this.deviceInformationForm,
    id: 'pipeDn',
    isTranslated: true,
    label: 'common.input.pipeDn.label',
    readonly: true,
    iconLeft: 'open_in_full',
    options: [
      {
        label: '50',
        value: '50',
      },
      {
        label: '65',
        value: '65',
      },
      {
        label: '80',
        value: '80',
      },
      {
        label: '100',
        value: '100',
      },
      {
        label: '125',
        value: '125',
      },
      {
        label: '150',
        value: '150',
      },
      {
        label: '200',
        value: '200',
      },
    ],
  };

  public deviceRotationConfig: InputSelectButtonInterface = {
    formGroup: this.deviceInformationForm,
    isTranslated: true,
    label: 'common.input.rotation.label',
    iconLeft: 'screen_rotation_up',
    id: 'rotation',
    readonly: true,
    options: [
      {
        label: '↔ Horizontal',
        value: 'horizontal',
      },
      {
        isTranslated: true,
        label: 'common.input.rotation.values.vertical',
        value: 'vertical',
      },
    ],
  };

  public devicePipeTypeConfig: InputTextInterface = {
    formGroup: this.deviceInformationForm,
    isTranslated: true,
    label: 'common.input.pipeType.label',
    id: 'pipeType',
    readonly: true,
    type: 'text',
    iconLeft: 'plumbing',
  };

  public deviceRoomConfig: InputTextInterface = {
    formGroup: this.deviceInformationForm,
    isTranslated: true,
    label: 'common.input.locationRoom.label',
    id: 'locationRoom',
    readonly: true,
    type: 'text',
    iconLeft: 'room_preferences',
  };

  public deviceAlarmList: TableRowInterface[] = [];

  public imageUrls: string[] = [];
  public loading: boolean = false;
  private readonly destroy$: Subject<void> = new Subject<void>();

  constructor(
    private readonly _deviceService: DeviceService,
    private readonly _deviceProfileService: DeviceProfileService,
    private readonly _decoderService: DecoderService,
    private readonly _translateService: TranslateService,
    private readonly _logService: LogService,
  ) {
  }

  ngAfterViewInit(): void {
      this._logService.sendLog({
        target: LoggingTargetEnum.DEVICE,
        action: LoggingActionEnum.VIEW,
        description: 'Schaut Gerät im view-Modus an',
        target_id: this.device?.id,
      });
    }

  private _device: DeviceDto | null = null;

  public get device(): DeviceDto | null {
    return this._device;
  }

  @Input()
  set device(value: DeviceDto | null) {
    this._device = value;

    if (!this._device) {
      this.deviceAlarmList = [];

      return;
    }

    this.imageUrls = [];
    this.deviceAlarmList = [];

    this._fetchDevice()
      .then();
  }

  public ngOnInit(): void {
    this._deviceService.devices$
      .pipe(takeUntil(this.destroy$))
      .subscribe((devices: DeviceDto[]) => {
        if (!this._device) {
          return;
        }

        const updatedDevice: DeviceDto | undefined = devices.find((device: DeviceDto) =>
          device.id === this._device!.id,
        );

        if (!updatedDevice) {
          return;
        }

        this._device = updatedDevice;
        this._fillForm();
      });
  }

  private _fillForm(): void {
    if (!this._device) {
      return;
    }

    this.imageUrls = this._device.images?.map((image: DeviceImageDto) => image.path) ?? [];

    const deviceProfile: DeviceProfileDto | null = this._device.device_profile_id
      ? this._deviceProfileService.getDeviceProfileById(this._device.device_profile_id)
      : null;

    const decoder: DecoderDto | null = this._device.decoder_id
      ? this._decoderService.getDecoderById(this._device.decoder_id)
      : null;

    this.deviceInformationForm.patchValue({
      serial_number: this._device.serial_number,
      device_eui: this._device.device_eui,
      manufacturer: deviceProfile?.manufacturer ?? '',
      model: deviceProfile?.model ?? '',
      decoder: decoder?.name ?? '',
      address: this._device.position?.address ?? '',
      installed_at: this._device.position?.installed_at
        ? dateToString(this._device.position?.installed_at)
        : 'N/A',
      installation_district: this._device.installation_district ?? '',
      last_sig_send: this._device.last_sigi_send_time
        ? formatTimeAgoTranslation(this._translateService,this._device.last_sigi_send_time)
        : 'N/A',
      comments: this._device.comments,
      locationRoom: this._device.metadata?.locationRoom,
      pipeSize: this._device.metadata?.pipeSize,
      pipeType: this._device.metadata?.pipeType,
      rotation: this._device.metadata?.rotation,
      pipeDn: this._device.metadata?.pipeDn?.toString(),
    });
  }

  private async _fetchDevice(): Promise<void> {
    if (!this._device) return

    const fullDevice: DeviceDto | null = await this._deviceService.getById(this._device.id);

    if (!fullDevice) {
      console.error('Error fetching device');

      return;
    }

    this._device = fullDevice;
    this._fillForm();
    this.loading = false;
  }
}
